import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { errorReportingService } from '@/services/error-reporting';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  level?: 'root' | 'page' | 'section' | 'component';
  resetKeys?: any[]; // Auto-reset when these values change
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // Update state with error details
    this.setState(prev => ({
      error,
      errorInfo,
      errorCount: prev.errorCount + 1,
    }));

    // Report error to backend (this will handle logging)
    errorReportingService.reportError(
      error,
      errorInfo,
      this.props.level || 'component',
      {
        errorCount: this.state.errorCount + 1,
        resetKeys: this.props.resetKeys,
      }
    );
  }

  componentDidUpdate(prevProps: Props) {
    // Auto-reset when resetKeys change
    if (this.state.hasError && this.props.resetKeys) {
      const hasResetKeyChanged = this.props.resetKeys.some(
        (key, index) => key !== prevProps.resetKeys?.[index]
      );
      
      if (hasResetKeyChanged) {
        this.reset();
      }
    }
  }

  reset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  reload = () => {
    window.location.reload();
  };

  goHome = () => {
    window.location.href = '/workflows';
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Otherwise, show default error UI based on level
      return this.renderDefaultErrorUI();
    }

    return this.props.children;
  }

  private renderDefaultErrorUI() {
    const { level = 'component' } = this.props;
    const { error, errorInfo, errorCount } = this.state;

    // Root level: Full-screen error
    if (level === 'root') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full space-y-4">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
              <p className="text-muted-foreground mb-6">
                The application encountered an unexpected error. Please try refreshing the page.
              </p>
            </div>

            <Alert variant="destructive">
              <AlertTitle>Error Details</AlertTitle>
              <AlertDescription className="mt-2 font-mono text-xs overflow-auto max-h-32">
                {error?.message || 'Unknown error'}
              </AlertDescription>
            </Alert>

            <div className="flex gap-3">
              <Button onClick={this.reload} className="flex-1">
                <RefreshCw className="mr-2 h-4 w-4" />
                Reload Page
              </Button>
              <Button variant="outline" onClick={this.goHome} className="flex-1">
                <Home className="mr-2 h-4 w-4" />
                Go Home
              </Button>
            </div>

            {import.meta.env.DEV && errorInfo && (
              <details className="mt-4 text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Stack Trace (Development Only)
                </summary>
                <pre className="mt-2 p-2 bg-muted rounded overflow-auto max-h-48 text-xs">
                  {errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    // Page level: Section error with recovery
    if (level === 'page') {
      return (
        <div className="flex items-center justify-center h-full p-8">
          <Alert variant="destructive" className="max-w-lg">
            <AlertTriangle className="h-5 w-5" />
            <AlertTitle>Page Error</AlertTitle>
            <AlertDescription className="mt-2">
              <p className="mb-4">This page encountered an error and couldn't load properly.</p>
              <p className="text-sm font-mono mb-4">{error?.message}</p>
              <div className="flex gap-2">
                <Button size="sm" onClick={this.reset}>
                  Try Again
                </Button>
                <Button size="sm" variant="outline" onClick={this.goHome}>
                  Go to Workflows
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    // Section level: Inline error box
    if (level === 'section') {
      return (
        <Alert variant="destructive" className="m-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Section Error</AlertTitle>
          <AlertDescription className="mt-2">
            <p className="text-sm mb-3">{error?.message || 'This section failed to load.'}</p>
            <Button size="sm" variant="outline" onClick={this.reset}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      );
    }

    // Component level: Minimal error message
    return (
      <div className="p-4 border border-destructive rounded-md bg-destructive/10">
        <div className="flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0" />
          <span className="font-medium">Component Error</span>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {error?.message || 'This component failed to render.'}
        </p>
        {errorCount < 3 && (
          <Button size="sm" variant="ghost" onClick={this.reset} className="mt-2">
            Retry
          </Button>
        )}
        {errorCount >= 3 && (
          <p className="text-xs text-destructive mt-2">
            Failed {errorCount} times. Please refresh the page.
          </p>
        )}
      </div>
    );
  }
}
